import { clsx } from 'clsx';
import { type PropsWithChildren, type ReactNode } from 'react';
import { Icon } from '#common/components/Icon';
import { Tooltip } from '#common/components/Tooltip.js';
import { renderDataAttributes, type PropsWithDataAttributes } from '#common/components/types.ts';

interface Props {
    icon: string;
    info?: ReactNode;
    title: ReactNode;
    subTitle: ReactNode;
    isSelected: boolean;
    onClick: () => void;
    className?: string;
    sizeClassNames: string;
}

export function Tile({
    icon,
    info,
    title,
    subTitle,
    isSelected,
    className,
    sizeClassNames,
    children,
    onClick,
    dataAttributes,
}: PropsWithDataAttributes<PropsWithChildren<Props>>) {
    return (
        <div
            className={clsx(
                sizeClassNames,
                'rounded-xl py-6 px-4 flex flex-col shadow-xl hover:shadow-gray-500 cursor-default',
                isSelected
                    ? 'bg-radial-[60%_70%_at_12%_25%] from-crispybluedark-600 to-crispybluedark-700'
                    : 'bg-white',
                isSelected ? 'text-white' : 'text-black',
                className
            )}
            onClick={onClick}
            role='button'
            {...renderDataAttributes(dataAttributes)}
        >
            <div className='flex flex-row text-3xl lg:text-4xl leading-0'>
                <Icon
                    icon={icon}
                    className={clsx('mr-auto shrink-0', isSelected ? 'text-white' : 'text-crispybluedark-600')}
                />
                {info && (
                    <Tooltip
                        trigger={
                            <Icon
                                icon='heroicons--information-circle'
                                className={clsx(
                                    'cursor-help shrink-0',
                                    isSelected ? 'text-white' : 'text-crispybluedark-600'
                                )}
                            />
                        }
                    >
                        <div className='whitespace-nowrap'>{info}</div>
                    </Tooltip>
                )}
            </div>
            <div className='text-3xl font-extrabold mt-2'>{title}</div>
            <p className='text-lg mb-2'>{subTitle}</p>
            {children}
        </div>
    );
}
